import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ClipboardList, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

const ACTION_LABELS = {
  login_success: 'Login',
  login_failed: 'Failed Login',
  password_changed: 'Password Changed',
  consent_updated: 'Consent Updated',
  data_export: 'Data Export',
  data_deletion_request: 'Deletion Request',
  data_uploaded: 'Data Upload',
  user_created: 'User Created',
  user_updated: 'User Updated',
  ai_anonymisation: 'AI Anonymisation',
};

export default function AuditLog() {
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState('all');
  const [emailSearch, setEmailSearch] = useState('');

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 50 });
      if (actionFilter !== 'all') params.append('action', actionFilter);
      if (emailSearch.trim()) params.append('user_email', emailSearch.trim());
      const res = await api.get(`/gdpr/audit-log?${params}`);
      setEntries(res.data.entries || []);
      setTotal(res.data.total || 0);
    } catch {
      toast.error('Failed to load audit log');
    } finally {
      setLoading(false);
    }
  }, [page, actionFilter, emailSearch]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const totalPages = Math.ceil(total / 50);

  const statusColor = (status) =>
    status === 'success' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
      : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300';

  return (
    <div className="space-y-4" data-testid="audit-log-page">
      <div>
        <h1 className="text-xl font-bold tracking-tight flex items-center gap-2">
          <ClipboardList className="w-7 h-7 text-primary" />
          Audit Log
        </h1>
        <p className="text-muted-foreground mt-1">GDPR-compliant activity trail</p>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by email..."
                className="pl-9"
                value={emailSearch}
                onChange={(e) => { setEmailSearch(e.target.value); setPage(1); }}
                data-testid="audit-email-search"
              />
            </div>
            <Select value={actionFilter} onValueChange={(v) => { setActionFilter(v); setPage(1); }}>
              <SelectTrigger className="w-48" data-testid="audit-action-filter">
                <SelectValue placeholder="Filter by action" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Actions</SelectItem>
                {Object.keys(ACTION_LABELS).map((k) => (
                  <SelectItem key={k} value={k}>{ACTION_LABELS[k]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
        <Card data-testid="audit-log-table">
          <CardHeader>
            <CardTitle>Activity Log</CardTitle>
            <CardDescription>{total} total entries</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8"><div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full" /></div>
            ) : entries.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">No audit entries found.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-3 px-3 text-xs font-semibold text-muted-foreground uppercase">Timestamp</th>
                      <th className="text-left py-3 px-3 text-xs font-semibold text-muted-foreground uppercase">Action</th>
                      <th className="text-left py-3 px-3 text-xs font-semibold text-muted-foreground uppercase">User</th>
                      <th className="text-left py-3 px-3 text-xs font-semibold text-muted-foreground uppercase">Details</th>
                      <th className="text-left py-3 px-3 text-xs font-semibold text-muted-foreground uppercase">IP</th>
                      <th className="text-center py-3 px-3 text-xs font-semibold text-muted-foreground uppercase">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {entries.map((e, i) => (
                      <tr key={i} className="border-b border-border hover:bg-muted/50 transition-colors">
                        <td className="py-3 px-3 text-xs font-mono whitespace-nowrap">
                          {e.timestamp ? new Date(e.timestamp).toLocaleString() : '-'}
                        </td>
                        <td className="py-3 px-3 text-sm font-medium">
                          {ACTION_LABELS[e.action] || e.action}
                        </td>
                        <td className="py-3 px-3 text-sm truncate max-w-[180px]">{e.user_email || '-'}</td>
                        <td className="py-3 px-3 text-sm text-muted-foreground truncate max-w-[240px]">{e.details || '-'}</td>
                        <td className="py-3 px-3 text-xs font-mono">{e.ip_address || '-'}</td>
                        <td className="py-3 px-3 text-center">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColor(e.status)}`}>
                            {e.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-between items-center mt-4">
                <p className="text-sm text-muted-foreground">Page {page} of {totalPages}</p>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
